/*
|--------------------------------------------------------------------------
| Utility: deleteHelper.js
| Author: Sayed Ahmed
| Description: Provides helper functions for deleting single or multiple
|              entities with email confirmation and Redux dispatching.
|              User must enter their account email to confirm deletion.
|--------------------------------------------------------------------------
*/

import Swal from "sweetalert2";

/**
 * Get current logged-in user email from localStorage.
 * @returns {string}
 */
function getCurrentUserEmail() {
  try {
    const user = JSON.parse(localStorage.getItem("user") || "{}");
    return (user?.email || "").trim().toLowerCase();
  } catch {
    return "";
  }
}

/**
 * Show email confirmation dialog. Resolves with true if email matches, false if cancelled.
 * @param {Object} options
 * @param {string} options.entityName - e.g. "Product", "Category"
 * @param {boolean} options.isBulk - whether deleting multiple items
 * @param {number} [options.count] - number of items (for bulk)
 * @param {Function} options.t - translation function
 * @returns {Promise<boolean>}
 */
async function confirmDeleteWithEmail({ entityName = "item", isBulk = false, count = 0, t = (x) => x }) {
  const currentEmail = getCurrentUserEmail();
  if (!currentEmail) {
    await Swal.fire({
      icon: "warning",
      title: t("Session"),
      text: t("Unable to verify user. Please log in again."),
    });
    return false;
  }

  const title = isBulk
    ? t("Confirm deletion of {{count}} {{entityName}}", { count, entityName })
    : t("Confirm {{entityName}} deletion", { entityName });
  const label = t("Enter your account email to confirm deletion");

  const result = await Swal.fire({
    title,
    html: `<p class="mb-3">${label}</p>`,
    input: "email",
    inputLabel: t("Your email"),
    inputPlaceholder: t("email@example.com"),
    showCancelButton: true,
    confirmButtonText: t("Delete"),
    cancelButtonText: t("Cancel"),
    confirmButtonColor: "#dc3545",
    inputValidator: (value) => {
      const entered = (value || "").trim().toLowerCase();
      if (!entered) return t("Email is required.");
      if (entered !== currentEmail) return t("Email does not match your account.");
      return null;
    },
  });

  return result.isConfirmed === true;
}

/**
 * Handles single entity deletion with email confirmation.
 *
 * @param {Object} params
 * @param {number|string} params.id - The ID of the entity to delete.
 * @param {string} [params.entityName="item"] - Entity display name.
 * @param {Function} params.deleteFunc - Redux thunk or action creator.
 * @param {Function} params.dispatch - Redux dispatch function.
 * @param {Function} [params.refetchFunc] - Optional function to re-fetch the data after deletion.
 * @param {Function} [params.t] - Translation function (defaults to identity).
 */
export async function handleDeleteEntity({
  id,
  entityName = "item",
  deleteFunc,
  dispatch,
  refetchFunc,
  t = (txt) => txt,
}) {
  const allowed = await confirmDeleteWithEmail({ entityName, isBulk: false, t });
  if (!allowed) return;

  const result = await dispatch(deleteFunc(id));
  if (result.payload?.success) {
    if (refetchFunc) dispatch(refetchFunc());
  }
}

/**
 * Handles bulk deletion with email confirmation.
 *
 * @param {Object} params
 * @param {Array<number|string>} params.ids - Array of IDs to delete.
 * @param {string} [params.entityName="items"] - Entity plural name.
 * @param {Function} params.deleteFunc - Redux thunk or action creator.
 * @param {Function} params.dispatch - Redux dispatch function.
 * @param {Function} [params.refetchFunc] - Optional function to re-fetch data.
 * @param {Function} [params.t] - Translation function.
 * @param {Function} [params.onSuccess] - Callback after successful deletion.
 */
export async function handleBulkDeleteEntities({
  ids = [],
  entityName = "items",
  deleteFunc,
  dispatch,
  refetchFunc,
  t = (txt) => txt,
  onSuccess = () => {},
}) {
  if (ids.length === 0) return;

  const allowed = await confirmDeleteWithEmail({
    entityName,
    isBulk: true,
    count: ids.length,
    t,
  });
  if (!allowed) return;

  const result = await dispatch(deleteFunc({ data: { ids } }));
  if (result.payload?.success) {
    if (refetchFunc) dispatch(refetchFunc());
    onSuccess(result.payload);
  }
}
